import type { ThemeClickPlacement } from './themeOrder';
import type { BoxTheme } from '@lub0v/santa-utils';
import { trackEvent } from './analytics';
import { trackUmamiEvent } from './umami';

export type ThemeAnalyticsContext = 'creation' | 'settings' | 'profile' | 'home';

const creationFirstClicks = new Set<string>();
const HOME_THEME_KEY = 'ss:last-home-theme:v1';
const FIRST_HOME_THEME_KEY = 'ss:first-home-theme:v1';
let firstHomeTheme = 'unknown';
let lastHomeTheme = 'unknown';

/** Store the applied homepage theme, including the initial default. */
export function rememberHomeTheme(theme: BoxTheme): void {
  firstHomeTheme = readFirstHomeTheme();
  if (firstHomeTheme === 'unknown') {
    firstHomeTheme = theme;
    try { window.sessionStorage.setItem(FIRST_HOME_THEME_KEY, theme); } catch { /* Optional storage. */ }
  }
  lastHomeTheme = theme;
  try { window.sessionStorage.setItem(HOME_THEME_KEY, theme); } catch { /* Optional storage. */ }
}
/** First applied homepage theme in this tab session; never replaced by later selections. */
export function readFirstHomeTheme(): string {
  try { return window.sessionStorage.getItem(FIRST_HOME_THEME_KEY) || firstHomeTheme; }
  catch { return firstHomeTheme; }
}
export function readLastHomeTheme(): string {
  try { return window.sessionStorage.getItem(HOME_THEME_KEY) || lastHomeTheme; }
  catch { return lastHomeTheme; }
}

/** The draft key is used locally for deduplication and is never sent to analytics. */
export function trackCreationFirstThumbnail(theme: BoxTheme, draftKey: string, homeTheme: string, placement?: ThemeClickPlacement, firstTheme: string = readFirstHomeTheme()): void {
  if (!draftKey || creationFirstClicks.has(draftKey)) return;
  creationFirstClicks.add(draftKey);
  try {
    const key = 'ss:creation-first-thumbnail:v1:' + draftKey;
    if (window.sessionStorage.getItem(key) !== null) return;
    window.sessionStorage.setItem(key, theme);
  } catch { /* Fall back to the loaded page when storage is blocked. */ }
  sendThemeEvent('theme_thumbnail_first_clicked_creation_v2', { context: 'creation', theme, home_theme: homeTheme, first_home_theme: firstTheme, ...placement });
}

/** Records the destination after a homepage theme has successfully loaded. */
export function trackHomeThemeChanged(theme: BoxTheme): void {
  rememberHomeTheme(theme);
  sendThemeEvent('home_theme_changed_v2', { context: 'home', theme });
}

export function trackThemeThumbnailClick(theme: BoxTheme, context: ThemeAnalyticsContext, placement?: ThemeClickPlacement): void {
  sendThemeEvent('theme_thumbnail_clicked_v2', { context, theme, ...placement });
}

/** One event per continuous thumbnail hold lasting at least one second. */
export function trackThemeThumbnailHold(theme: BoxTheme, context: ThemeAnalyticsContext): void {
  sendThemeEvent('theme_thumbnail_hold_v2', { context, theme });
}

export function trackThemePreview(theme: BoxTheme, context: ThemeAnalyticsContext): void {
  sendThemeEvent('theme_preview_clicked_v2', { context, theme });
}

export function trackThemeIconSelected(theme: BoxTheme, context: ThemeAnalyticsContext): void {
  sendThemeEvent('theme_icon_selected_v2', { context, theme });
}

export function trackThemeDayNightClick(theme: BoxTheme, context: ThemeAnalyticsContext): void {
  sendThemeEvent('theme_day_night_clicked_v2', { context, theme });
}

function sendThemeEvent(event: string, data: Record<string, string | number>): void {
  try { trackEvent(event, data); } catch { /* Analytics must not block the picker. */ }
  try { void Promise.resolve(trackUmamiEvent(event, data)).catch(() => {}); } catch { /* Tracker unavailable. */ }
}
